import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { 
  CreateInterviewRequest, 
  CreateInterviewResponse, 
  TrainingInterviewResponse,
  SessionDetails,
  ExplainResponse,
  InterviewSubmitResponse,
  ProfileInterviewSession
} from '../models/virtual-interview-model';
import { Observable, of, throwError, timer } from 'rxjs';
import { tap, catchError, map, retryWhen, mergeMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class InterviewService {
  mainURL = 'api/training-interview';
  
  constructor(private http: HttpClient) {}

  getTrainingInterview(): Observable<TrainingInterviewResponse> {
    return this.http.get<TrainingInterviewResponse>(environment.apiUrl + this.mainURL);
  }

  getProfileInterviews(): Observable<ProfileInterviewSession[]> {
    return this.http.get<ProfileInterviewSession[]>(
      `${environment.apiUrl}api/profile/training-interview`
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        return of([]);
      })
    );
  }

  createTrainingInterview(
    data: CreateInterviewRequest, 
    cvFile: File | null,
    cvPath?: string
  ): Observable<CreateInterviewResponse> {
    const formData = new FormData();
    
    const requestData: CreateInterviewRequest = {
      jobTitle: data.jobTitle,
      jobDescription: data.jobDescription,
      keywords: data.keywords,
      instructions: data.instructions,
      interviewType: data.interviewType,
      language: data.language
    };

    if (cvPath && !cvFile) {
      requestData.cvPath = cvPath;
    }

    const dataBlob = new Blob([JSON.stringify(requestData)], { 
      type: 'application/json' 
    });
    formData.append('data', dataBlob);

    if (cvFile) {
      formData.append('cv', cvFile, cvFile.name);
    }

    return this.http.post<CreateInterviewResponse>(
      `${environment.apiUrl}${this.mainURL}-data`,
      formData
    );
  }

  getSessionDetails(sessionId: string): Observable<SessionDetails> {
    const url = `${environment.apiUrl}api/training-interview/session/${sessionId}`;
    
    return this.http.get<SessionDetails>(url);
  }

  getExplanation(sessionId: string, videoId: string): Observable<ExplainResponse> {
    return this.http.post<ExplainResponse>(
      `${environment.apiUrl}api/virtual-interview/${sessionId}/explain/${videoId}`,
      {}
    );
  }

  initiateRecording(sessionId: string, interviewVideoId: string): Observable<string> {
    const params = {
      sessionId,
      interviewVideoId
    };
    
    return this.http.post(
      `${environment.apiUrl}api/training-interview/initiate-recording`,
      null,
      { 
        params, 
        responseType: 'text'
      }
    ).pipe(
      map(response => {
        return response.replace(/"/g, '');
      })
    );
  }

  appendChunk(
    videoId: string,
    chunk: Blob,
    currentTotalDuration: number,
    chunkIndex: number,
    attempt: number = 0
  ): Observable<void> {
    const MAX_RETRIES = 5;
    const RETRY_DELAY_MS = 1500;

    const formData = new FormData();
    const cleanVideoId = videoId.replace(/"/g, '');
    const durationInSeconds = Math.floor(currentTotalDuration / 1000);
    const videoBlob = new Blob([chunk], { type: 'video/webm' });

    formData.append('videoId', cleanVideoId);
    formData.append('chunk', videoBlob, `chunk_${chunkIndex}.webm`);
    formData.append('currentTotalDuration', durationInSeconds.toString());
    formData.append('chunkIndex', chunkIndex.toString());

    return this.http.post<void>(
      `${environment.apiUrl}api/training-interview/append-chunk`,
      formData
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        // Dig into every possible location the message could be
        const raw = [
          error?.error?.message,
          error?.error?.error,
          error?.error,
          error?.message,
        ]
          .map(v => (typeof v === 'string' ? v : JSON.stringify(v ?? '')))
          .join(' ');

        const isOrderError = /order/i.test(raw);

        console.log('Chunk upload error raw:', raw, '| isOrderError:', isOrderError);

        if (isOrderError && attempt < MAX_RETRIES) {
          console.warn(
            `Chunk ${chunkIndex} order error – retrying (${attempt + 1}/${MAX_RETRIES}) in ${RETRY_DELAY_MS}ms...`
          );
          return new Observable<void>(observer => {
            setTimeout(() => {
              this.appendChunk(videoId, chunk, currentTotalDuration, chunkIndex, attempt + 1)
                .subscribe({
                  next: () => { observer.next(); observer.complete(); },
                  error: (err) => observer.error(err)
                });
            }, RETRY_DELAY_MS);
          });
        }

        return throwError(() => error);
      })
    );
  }
  appendAdditionalChunk(
    videoId: string,
    chunk: Blob,
    currentTotalDuration: number,
    chunkIndex: number,
    attempt: number = 0
  ): Observable<void> {
    const MAX_RETRIES = 5;
    const RETRY_DELAY_MS = 1500;

    const formData = new FormData();
    const cleanVideoId = videoId.replace(/"/g, '');
    const durationInSeconds = Math.floor(currentTotalDuration / 1000);
    const videoBlob = new Blob([chunk], { type: 'video/webm' });

    formData.append('videoId', cleanVideoId);
    formData.append('chunk', videoBlob, `chunk_${chunkIndex}.webm`);
    formData.append('currentTotalDuration', durationInSeconds.toString());
    formData.append('chunkIndex', chunkIndex.toString());

    return this.http.post<void>(
      `${environment.apiUrl}api/training-interview/append-chunk-additional`,
      formData
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        const raw = [
          error?.error?.message,
          error?.error?.error,
          error?.error,
          error?.message,
        ]
          .map(v => (typeof v === 'string' ? v : JSON.stringify(v ?? '')))
          .join(' ');

        const isOrderError = /order/i.test(raw);

        console.log('Additional chunk upload error raw:', raw, '| isOrderError:', isOrderError);

        if (isOrderError && attempt < MAX_RETRIES) {
          console.warn(
            `Additional chunk ${chunkIndex} order error – retrying (${attempt + 1}/${MAX_RETRIES}) in ${RETRY_DELAY_MS}ms...`
          );
          return new Observable<void>(observer => {
            setTimeout(() => {
              this.appendAdditionalChunk(videoId, chunk, currentTotalDuration, chunkIndex, attempt + 1)
                .subscribe({
                  next: () => { observer.next(); observer.complete(); },
                  error: (err) => observer.error(err)
                });
            }, RETRY_DELAY_MS);
          });
        }

        return throwError(() => error);
      })
    );
  }
  finishRecording(videoId: string): Observable<void> {
    const cleanVideoId = videoId.replace(/"/g, '');
    
    return this.http.post<void>(
      `${environment.apiUrl}api/training-interview/${cleanVideoId}/finish`,
      {}
    ).pipe(
      catchError((error: HttpErrorResponse) => {
        console.error('Finish recording API error:', error);
        // Don't block user flow if finish fails
        return of(void 0);
      })
    );
  }

  submitInterview(sessionId: string): Observable<InterviewSubmitResponse> {
    return this.http.post<InterviewSubmitResponse>(
      `${environment.apiUrl}api/training-interview/${sessionId}/submit`,
      {}
    );
  }

  private isStaticAssetPath(path: string): boolean {
    const staticPaths = [
      'assets/img/',
      'assets/images/',
      'assets/icons/',
      '/assets/img/',
      '/assets/images/',
      '/assets/icons/'
    ];
    return staticPaths.some(staticPath => path.includes(staticPath));
  }

  private loadAudioWithAuth(audioUrl: string, token: string): Observable<string> {
    const headers = new HttpHeaders({
      Authorization: `Bearer ${token}`
    });

    return this.http
      .get(audioUrl, {
        headers,
        responseType: 'blob'
      })
      .pipe(
        map((blob: Blob) => {
          return URL.createObjectURL(blob);
        })
      );
  }

  private loadAudioWithoutAuth(audioUrl: string): Observable<string> {
    return this.http
      .get(audioUrl, {
        responseType: 'blob'
      })
      .pipe(
        map((blob: Blob) => {
          return URL.createObjectURL(blob);
        }),
        catchError((error: HttpErrorResponse) => {
          return of('');
        })
      );
  }

  getSecureAudio(audioUrl: string): Observable<string> {
    const isStaticAsset = this.isStaticAssetPath(audioUrl);

    if (isStaticAsset) {
      return this.loadAudioWithoutAuth(audioUrl);
    }

    const token = localStorage.getItem('accessToken');

    if (!token) {
      return this.loadAudioWithoutAuth(audioUrl);
    }

    return this.loadAudioWithAuth(audioUrl, token).pipe(
      catchError((error: HttpErrorResponse) => {
        if ([500, 401, 403].includes(error.status)) {
          return this.loadAudioWithoutAuth(audioUrl);
        }

        return of('');
      })
    );
  }
}